import type { SearchParamsObject } from '@algolia/client-search';

import { configString } from 'config/appConfigUtils';
import type { OrgDashboardAlgoliaHit } from 'modules/orgDashboard/algolia/types/OrgDashboardAlgoliaHit';
import { searchAlgoliaConfig } from 'modules/search/algolia/searchAlgoliaConfig';
import { algoliaSearch } from 'utils/algolia/algoliaSearch';

function getQueryOptions({
  type,
  status,
  pageIndex,
}: {
  type: string;
  status: string;
  pageIndex: number;
}): SearchParamsObject {
  const filters = [];

  if (type === 'VOLOP') {
    filters.push(`type:'${type}' AND actionType:'VOLOP'`);
  } else if (type !== 'ALL') {
    filters.push(`type:'${type}'`);
  }

  if (status !== 'ALL') {
    filters.push(`status:'${status}'`);
  }

  const attributesToRetrieve = [
    'objectID',
    'type',
    'status',
    'name',
    'city',
    'state',
    'country',
    'published',
    'created',
    'expires',
    'updated',
    'uniqueViews',
    'applicationCount',
    'hasNewActivity',
    'canPromote',
  ];

  return {
    hitsPerPage: 20,
    page: pageIndex,
    attributesToRetrieve,
    filters: filters.join(' AND '),
  } satisfies SearchParamsObject;
}

type Args = {
  searchApiKey: string;
  pageIndex: number;
  status: string;
  query: string;
  type: string;
};

export async function orgDashboardManageListingsIsomorphicSearch({
  searchApiKey,
  pageIndex,
  status,
  query,
  type,
}: Args) {
  const config = searchAlgoliaConfig(searchApiKey);
  const queryOptions = getQueryOptions({ type, status, pageIndex });

  const [result, searchStatusesResult] = await Promise.all([
    algoliaSearch<OrgDashboardAlgoliaHit>({
      config,
      query,
      indexName: configString('algolia', 'orgIndexName'),
      options: queryOptions,
      analytics: true,
    }),

    algoliaSearch<OrgDashboardAlgoliaHit>({
      config,
      query: '',
      indexName: configString('algolia', 'orgIndexName'),
      options: {
        attributesToRetrieve: ['status'],
        hitsPerPage: 0,
        facets: ['status'],
      },
      analytics: false,
    }),
  ]);

  const validStatuses = Object.keys(searchStatusesResult.facets?.status ?? {});

  return { result, validStatuses };
}
